import type { FamilyActivityEntry, FamilyActivitySource } from 'apis/master-product-families';

// Pure formatting helpers for the family Activity panel — turn one BE
// activity-log row into a human-readable line + optional source badge.
// No React/i18n here; keep this file dependency-free and easy to unit test.

const FIELD_LABELS: Record<string, string> = {
  baseUnit: 'Fundamental Unit',
  netContent: 'Base quantity',
  packUnit: 'Packaging Unit',
  packSize: 'Package quantity',
  catalogCategoryId: 'Category',
  familyId: 'Family',
  family: 'Family',
  universalName: 'Name',
  isActive: 'Active',
  itemUnit: 'Base Unit',
  imageUrl: 'Image',
};

// camelCase (or plain lowercase) → Title Case, e.g. 'catalogCategoryName' →
// 'Catalog Category Name'.
function toTitleCase(field: string): string {
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function humanizeField(field: string): string {
  return FIELD_LABELS[field] ?? toTitleCase(field);
}

const ATTRIBUTE_FIELD_PREFIX = 'attribute.';

const dash = (value: string | null): string => value ?? '—';

export function formatActivityLine(entry: FamilyActivityEntry): string {
  switch (entry.action) {
    case 'created':
      return 'Created';
    case 'deleted':
      return 'Deleted';
    case 'merged':
      return entry.newValue ?? 'Merged';
    case 'updated': {
      const field = entry.field ?? '';
      if (field.startsWith(ATTRIBUTE_FIELD_PREFIX)) {
        const type = field.slice(ATTRIBUTE_FIELD_PREFIX.length);
        return `Attribute ${toTitleCase(type)}: ${dash(entry.oldValue)} → ${dash(entry.newValue)}`;
      }
      return `${humanizeField(field)}: ${dash(entry.oldValue)} → ${dash(entry.newValue)}`;
    }
    default:
      return '';
  }
}

const SOURCE_BADGES: Record<Exclude<FamilyActivitySource, 'cms'>, string> = {
  bulk_edit: 'Bulk edit',
  sheet_upload: 'Sheet upload',
  merge: 'Merge',
};

export function sourceBadge(source: FamilyActivitySource): string | null {
  if (source === 'cms') return null;
  return SOURCE_BADGES[source];
}
